import { controlKeys, entityKeys } from "../../../../api/queryKeys";

/**
 * Creates optimistic update handlers for entity mutations (labels/tags).
 * Provides instant UI feedback while the server request is in flight.
 */
export function createOptimisticMutationHandlers({
  queryClient,
  selectedEntities,
  fieldName,
  successMessage,
  errorMessage,
}) {
  return {
    /**
     * Prepares optimistic update by caching previous state and updating UI immediately.
     */
    onMutate: async ({ idsToAdd, idsToRemove }) => {
      await queryClient.cancelQueries({ queryKey: entityKeys.all });

      const previousEntityQueries = {};
      const selectedEntitiesTypes = Array.from(
        new Set(selectedEntities.map((entity) => entity.entity_type))
      );

      for (const entityType of selectedEntitiesTypes) {
        const queryKey = entityKeys.entitiesByType(entityType);
        const previousData = queryClient.getQueryData(queryKey);
        previousEntityQueries[entityType] = previousData;

        if (previousData) {
          queryClient.setQueryData(queryKey, (old) => {
            if (!old) return old;

            const entities = old.entities || old;
            const updatedEntities = (
              Array.isArray(entities) ? entities : []
            ).map((entity) => {
              if (
                selectedEntities.some((se) => se.entity_id === entity.entity_id)
              ) {
                const currentValues = entity[fieldName] || [];
                const newValues = [
                  ...new Set([
                    ...currentValues.filter((v) => !idsToRemove.includes(v)),
                    ...idsToAdd,
                  ]),
                ];
                return { ...entity, [fieldName]: newValues };
              }
              return entity;
            });

            return Array.isArray(entities)
              ? updatedEntities
              : { ...old, entities: updatedEntities };
          });
        }
      }

      return { previousEntityQueries, selectedEntitiesTypes };
    },

    /**
     * Rollback to previous state on error.
     */
    onError: (error, variables, context) => {
      if (context?.previousEntityQueries) {
        for (const [entityType, previousData] of Object.entries(
          context.previousEntityQueries
        )) {
          queryClient.setQueryData(
            entityKeys.entitiesByType(entityType),
            previousData
          );
        }
      }
    },

    /**
     * Invalidate relevant queries after mutation settles to sync with server.
     */
    onSettled: (data, error, variables, context) => {
      context?.selectedEntitiesTypes?.forEach((entityType) => {
        queryClient.invalidateQueries({
          queryKey: entityKeys.entitiesByType(entityType),
        });
      });
      queryClient.invalidateQueries({ queryKey: entityKeys.allEntities() });
      queryClient.invalidateQueries({ queryKey: controlKeys.all });
      queryClient.invalidateQueries({
        queryKey: entityKeys.entitiesOverview(),
      });
    },

    successMessage,
    errorMessage,
  };
}

/**
 * Computes which IDs changed based on checkbox state diff.
 */
export function computeChangedIds(
  checkboxStates,
  initialCheckboxStates,
  checkedState,
  uncheckedState
) {
  const idsToAddSet = new Set();
  const idsToRemoveSet = new Set();

  for (const [id, checkboxState] of Object.entries(checkboxStates)) {
    if (checkboxState === initialCheckboxStates[id]) continue;
    if (checkboxState === checkedState) idsToAddSet.add(id);
    if (checkboxState === uncheckedState) idsToRemoveSet.add(id);
  }

  return {
    idsToAdd: [...idsToAddSet],
    idsToRemove: [...idsToRemoveSet],
  };
}
